'use strict';

const Button = require('./components/Button');
const { initLocaleSwitcher } = require('./components/LocaleSwitcher');

const ENDPOINTS = {
    requestOtp: '',
    verifyOtp: '',
    validateSession: '',
    ocapiSessionsUrl: '',
    bridgeSession: ''
};
const passwordlessState = { email: null };
const GENERAL_ERROR_MESSAGE = 'An error occurred. Please try again.';
const NETWORK_ERROR_MESSAGE = 'Network error. Please check your connection.';
const VISIBLE_CLASS = 'is-visible';

document.addEventListener('DOMContentLoaded', () => {
    const passwordlessForm = document.getElementById('reward-passwordless-form');
    if (passwordlessForm) {
        passwordlessForm.addEventListener('submit', handlePasswordlessSubmit);
    }

    const otpForm = document.getElementById('reward-otp-form');
    if (otpForm) {
        otpForm.addEventListener('submit', handleOtpSubmit);
    }

    const resendBtn = document.getElementById('resend-otp-btn');
    if (resendBtn) {
        resendBtn.addEventListener('click', handleResendOtp);
    }

    initLocaleSwitcher();

    const container = document.getElementById('passwordless-config');
    if (container) {
        ENDPOINTS.requestOtp = container.dataset.requestOtpUrl || '';
        ENDPOINTS.verifyOtp = container.dataset.verifyOtpUrl || '';
        ENDPOINTS.validateSession = container.dataset.validateSessionUrl || '';
        ENDPOINTS.ocapiSessionsUrl = container.dataset.ocapiSessionsUrl || '';
        ENDPOINTS.bridgeSession = container.dataset.bridgeSessionUrl || '';
    }
});

async function handlePasswordlessSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const emailInput = form.querySelector('input[name="loginEmail"]');
    const submitBtn = document.getElementById('request-otp-btn');
    const errorContainer = document.getElementById('login-error');
    const button = new Button(submitBtn);

    const email = emailInput.value.trim();
    if (!email) {
        showError(errorContainer, 'Please enter your email address.');
        return;
    }

    button.setLoading();
    hideError(errorContainer);

    try {
        const result = await requestPasswordlessLogin(email);
        button.removeLoading();

        if (result.success) {
            passwordlessState.email = email;
            button.setSuccess(null, 2000);

            showOtpForm();
        } else {
            button.setError('Error', 2300);
            showError(errorContainer, result.error || 'Failed to send verification code.');
        }
    } catch (error) {
        button.removeLoading();
        console.error('Passwordless login error:', error);
        button.setError('Error', 2300);
        showError(errorContainer, GENERAL_ERROR_MESSAGE);
    }
}

function showOtpForm() {
    const emailForm = document.getElementById('reward-passwordless-form');
    const otpForm = document.getElementById('reward-otp-form');
    const otpEmailDisplay = document.getElementById('otp-email-display');
    const backBtn = document.getElementById('back-to-email');

    if (backBtn) {
        backBtn.classList.remove('hide');

        backBtn.addEventListener('click', () => {
            backBtn.classList.add('hide');
            if (otpForm) {
                otpForm.style.display = 'none';
            }

            if (emailForm) {
                emailForm.style.display = 'flex';
            }
        });
    }

    if (emailForm) {
        emailForm.style.display = 'none';
    }

    if (otpForm) {
        otpForm.style.display = 'flex';
    }

    if (otpEmailDisplay && passwordlessState.email) {
        otpEmailDisplay.textContent = passwordlessState.email;
    }
}

async function requestPasswordlessLogin(email) {
    if (!ENDPOINTS.requestOtp) {
        return { success: false, error: 'Passwordless login is not configured.' };
    }

    try {
        const formData = new FormData();
        formData.append('email', email);

        const response = await fetch(ENDPOINTS.requestOtp, {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        return await response.json();
    } catch (error) {
        console.error('Request OTP error:', error);
        return { success: false, error: NETWORK_ERROR_MESSAGE };
    }
}

async function handleOtpSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const otpInput = form.querySelector('input[name="otp"]');
    const submitBtn = form.querySelector('[type="submit"]');
    const errorContainer = document.getElementById('otp-error');
    const successContainer = document.getElementById('otp-success');
    hideError(successContainer);
    const button = new Button(submitBtn);

    const otp = otpInput.value.trim();
    if (!otp) {
        showError(errorContainer, 'Please enter the verification code.');
        return;
    }

    button.setLoading();
    hideError(errorContainer);

    try {
        const verifyResult = await verifyOtp(passwordlessState.email, otp);
        if (!verifyResult.success) {
            button.setError('Error', 2300);
            showError(errorContainer, verifyResult.error || 'Invalid verification code.');
            return;
        }

        const bridgeResult = await bridgeSessionOcapi(verifyResult.accessToken);
        if (!bridgeResult.success) {
            button.setError('Error', 2300);
            showError(errorContainer, bridgeResult.error || 'Failed to establish session.');
            return;
        }

        const validateResult = await validateSession();
        if (validateResult.success) {
            button.setSuccess('Success', 3000);
            window.location.href = validateResult.redirectUrl;
        } else {
            button.setError('Error', 2300);
            showError(errorContainer, validateResult.error || 'Session validation failed.');
        }
    } catch (error) {
        console.error('OTP verification error:', error);
        button.setError('Error', 2300);
        showError(errorContainer, GENERAL_ERROR_MESSAGE);
    } finally {
        button.removeLoading();
    }
}

async function verifyOtp(email, otp) {
    if (!ENDPOINTS.verifyOtp) {
        return { success: false, error: 'Passwordless login is not configured.' };
    }

    try {
        const formData = new FormData();
        formData.append('email', email);
        formData.append('otp', otp);

        const response = await fetch(ENDPOINTS.verifyOtp, {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        return await response.json();
    } catch (error) {
        console.error('Verify OTP error:', error);
        return { success: false, error: NETWORK_ERROR_MESSAGE };
    }
}

async function bridgeSessionOcapi(accessToken) {
    try {
        const formData = new FormData();
        formData.append('accessToken', accessToken);

        const response = await fetch(ENDPOINTS.bridgeSession, {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        return await response.json();
    } catch (error) {
        console.error('Bridge session error:', error);
        return { success: false, error: NETWORK_ERROR_MESSAGE };
    }
}

async function validateSession() {
    if (!ENDPOINTS.validateSession) {
        return { success: false, error: 'Session validation is not configured.' };
    }

    try {
        const response = await fetch(ENDPOINTS.validateSession, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });

        return await response.json();
    } catch (error) {
        console.error('Validate session error:', error);
        return { success: false, error: NETWORK_ERROR_MESSAGE };
    }
}

async function handleResendOtp(e) {
    e.preventDefault();

    const btn = e.target;
    const errorContainer = document.getElementById('otp-error');
    const successContainer = document.getElementById('otp-success');
    const originalText = btn.innerHTML;

    if (!passwordlessState.email) {
        showError(errorContainer, 'Session expired. Please start over.');
        return;
    }

    btn.disabled = true;
    btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Sending...';

    try {
        const result = await requestPasswordlessLogin(passwordlessState.email);

        if (result.success) {
            if (successContainer) {
                successContainer.textContent = 'A new code has been sent to your email.';
                successContainer.classList.add(VISIBLE_CLASS);
            }
            hideError(errorContainer);
        } else {
            showError(errorContainer, result.error || 'Failed to resend code.');
        }
    } catch (error) {
        console.error('Resend OTP error:', error);
        showError(errorContainer, GENERAL_ERROR_MESSAGE);
    }

    btn.disabled = false;
    btn.innerHTML = originalText;
}

function showError(container, message) {
    if (container) {
        container.textContent = message;
        container.classList.add(VISIBLE_CLASS);
    }
}

function hideError(container) {
    if (container) {
        container.textContent = '';
        container.classList.remove(VISIBLE_CLASS);
    }
}
